/**
 * Cross-component registry of mounted video players, keyed by the field's
 * stable `field_id`. The comment composer (a separate Livewire island) reads
 * this to capture the current timestamp, and comment-row "jump" chips read it
 * to seek the player.
 *
 * This is a plain module-level Map — NEVER an Alpine.store or x-data — so the
 * player handles (and any $wire/proxy they might close over) are never made
 * reactive or JSON-serialized. See CLAUDE.md on the Livewire Proxy toJSON trap.
 *
 * A handle is: { fieldId, provider, label, canCapture, watchUrl,
 *                getCurrentTime(): number|null, seek(seconds): void }
 */
const players = new Map();

// The player keyboard shortcuts (jog/shuttle) act on — the one last played or,
// when there's only one video on the page, that one. Never auto-guessed across
// multiple idle players.
let activeFieldId = null;

function notifyChanged() {
    window.dispatchEvent(new CustomEvent('video-players:changed'));
}

export function registerPlayer(fieldId, handle) {
    players.set(String(fieldId), handle);
    notifyChanged();
}

export function unregisterPlayer(fieldId) {
    if (players.delete(String(fieldId))) {
        if (activeFieldId === String(fieldId)) activeFieldId = null;
        notifyChanged();
    }
}

/** Mark which player the keyboard shortcuts should drive (set when one plays). */
export function setActivePlayer(fieldId) {
    activeFieldId = String(fieldId);
}

/**
 * The field id the keyboard shortcuts target: the explicitly-active one, or the
 * sole player when there's exactly one. Returns null when several players exist
 * and none has been played yet — so we never hijack keys ambiguously.
 */
export function getActivePlayer() {
    if (activeFieldId && players.has(activeFieldId)) return activeFieldId;
    if (players.size === 1) return players.keys().next().value;
    return null;
}

export function getPlayer(fieldId) {
    return players.get(String(fieldId)) ?? null;
}

/** @returns {Array} the registered handles (used by the composer's field picker) */
export function listPlayers() {
    return Array.from(players.values());
}

export function clearPlayers() {
    players.clear();
    activeFieldId = null;
    notifyChanged();
}

/**
 * Seek a mounted player to `seconds`. Returns true if a player handled it,
 * false when no player is mounted for that field (caller can then fall back
 * to opening a deep link in a new tab).
 */
export function seekPlayer(fieldId, seconds) {
    const handle = getPlayer(fieldId);

    if (handle && typeof handle.seek === 'function') {
        handle.seek(Number(seconds) || 0);
        return true;
    }

    return false;
}

// Bridge for inline Blade Alpine (x-on:click), which can't import ES modules.
window.VideoPlayers = {
    get: getPlayer,
    list: listPlayers,
    seek: seekPlayer,
    setActive: setActivePlayer,
    getActive: getActivePlayer,
};

// ---- jog / shuttle keyboard shortcuts ------------------------------------
//
//   , / .     step ±1s        ← / →     jog ±5s
//   J / L     jog ±10s        K / Space play / pause
//
// Hard-gated: ignored when a modifier key is held (so app shortcuts like
// Ctrl+Enter still work), when no controllable player is active (no hijack on
// non-video pages or for Google Drive), and when focus is on a control that owns
// these keys itself — typing fields, buttons, links, and ARIA widgets — so e.g.
// Space still activates the focused button instead of toggling the video.

// Note: `[tabindex]` matches the ATTRIBUTE, so the focusable video iframe (which
// has no tabindex attribute) is not treated as an interactive control.
const INTERACTIVE_SELECTOR = 'a[href], button, input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="button"], [role="radio"], [role="tab"], [role="menuitem"], [role="checkbox"], [role="switch"], [tabindex]:not([tabindex="-1"])';

function isInteractiveTarget(el) {
    if (!el) return false;
    if (el.isContentEditable) return true;
    return !!(el.closest && el.closest(INTERACTIVE_SELECTOR));
}

async function jogActivePlayer(handle, delta) {
    const current = await handle.getCurrentTime();
    if (current === null || current === undefined) return;
    handle.seek(Math.max(0, current + delta));
}

function onPlayerKeydown(event) {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (players.size === 0) return;
    if (isInteractiveTarget(event.target) || isInteractiveTarget(document.activeElement)) return;

    let delta = null;
    let toggle = false;
    switch (event.key) {
        case ',': delta = -1; break;
        case '.': delta = 1; break;
        case 'ArrowLeft': delta = -5; break;
        case 'ArrowRight': delta = 5; break;
        case 'j': case 'J': delta = -10; break;
        case 'l': case 'L': delta = 10; break;
        case 'k': case 'K': case ' ': toggle = true; break;
        default: return;
    }

    const fieldId = getActivePlayer();
    if (!fieldId) return;
    const handle = getPlayer(fieldId);
    if (!handle || !handle.canSeek) return; // e.g. Google Drive — not seekable

    event.preventDefault();
    if (toggle) {
        if (typeof handle.togglePlay === 'function') handle.togglePlay();
    } else {
        jogActivePlayer(handle, delta);
    }
}

window.addEventListener('keydown', onPlayerKeydown);

// SPA navigation reuses the page; drop stale handles so we never seek a dead player.
window.addEventListener('livewire:navigating', clearPlayers);
